import {
  makeGetRequest,
  makeGetRequestBlob,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from 'lib/axios';
import refreshTokenId from 'utils/firebase.util';
import { url } from 'constants/api.constants';
import { PaginateResult, PaginationRequestPayload } from 'types';
import { BagelDocument, BagelDocumentCategory } from '../types';

export type { BagelDocumentCategory };

export interface BagelDocumentStatusSummary {
  pending: number;
  processing: number;
  ready: number;
  error: number;
  total: number;
}

export async function listBagelDocuments(): Promise<BagelDocument[]> {
  const res = await makeGetRequest<{
    message: string;
    payload: BagelDocument[];
  }>('/bagel/documents');
  return res.data.payload;
}

export type BagelDocumentPaginationFilters = {
  ingestSource: string;
  domain: string;
  category: string;
  status: string;
  year: string;
  fileType: string;
};

export async function getBagelDocumentsPagination(
  payload: PaginationRequestPayload,
  filters: BagelDocumentPaginationFilters,
): Promise<PaginateResult<BagelDocument>> {
  const q = new URLSearchParams({
    page: String(payload.page),
    limit: String(payload.limit),
    sortColumn: payload.sortColumn,
    sortType: payload.sortType,
    search: payload.search,
    ingestSource: filters.ingestSource,
    domain: filters.domain,
    category: filters.category,
    status: filters.status,
    year: filters.year,
    fileType: filters.fileType,
  });
  const res = await makeGetRequest<{
    message: string;
    payload: PaginateResult<BagelDocument>;
  }>(`/bagel/documents/pagination?${q.toString()}`);
  return res.data.payload;
}

export async function getBagelDocumentIngestYears(
  domain?: string,
): Promise<string[]> {
  const q = new URLSearchParams();
  if (domain && domain !== 'all') {
    q.set('domain', domain);
  }
  const query = q.toString();
  const path = query
    ? `/bagel/documents/ingest-years?${query}`
    : '/bagel/documents/ingest-years';
  const res = await makeGetRequest<{
    message: string;
    payload: string[];
  }>(path);
  return res.data.payload;
}

export async function getBagelDocumentStatusSummary(params?: {
  domain?: string;
  year?: string;
}): Promise<BagelDocumentStatusSummary> {
  const searchParams = new URLSearchParams();
  if (params?.domain) searchParams.set('domain', params.domain);
  if (params?.year) searchParams.set('year', params.year);
  const query = searchParams.toString();
  const path = query
    ? `/bagel/documents/status-summary?${query}`
    : '/bagel/documents/status-summary';

  const res = await makeGetRequest<{
    message: string;
    payload: BagelDocumentStatusSummary;
  }>(path);
  return res.data.payload;
}

export async function getBagelDocument(
  id: string,
): Promise<BagelDocument> {
  const res = await makeGetRequest<{
    message: string;
    payload: BagelDocument;
  }>(`/bagel/documents/${id}`);
  return res.data.payload;
}

export async function getBagelDocumentSignedUrl(
  id: string,
): Promise<{ url: string; expiresIn: number }> {
  const res = await makeGetRequest<{
    message: string;
    payload: { url: string; expiresIn: number };
  }>(`/bagel/documents/${id}/signed-url`);
  return res.data.payload;
}

export async function fetchBagelDocumentPreviewBlob(
  id: string,
): Promise<Blob> {
  const res = await makeGetRequestBlob(`/bagel/documents/${id}/preview`);
  return res.data;
}

export async function reprocessBagelDocument(id: string): Promise<void> {
  await makePostRequest(`/bagel/documents/${id}/reprocess`, {});
}

export async function deleteBagelDocument(id: string): Promise<void> {
  await makeDeleteRequest(`/bagel/documents/${id}`);
}

export interface UploadBagelDocumentParams {
  file: File;
  name: string;
  category: BagelDocumentCategory;
  description?: string;
}

export interface UpdateBagelDocumentParams {
  name?: string;
  description?: string;
  category?: BagelDocumentCategory;
  title?: string;
  doc_type?: string;
  tags?: string[];
  chunk_strategy?:
    | 'by_paragraph'
    | 'by_slide'
    | 'by_row'
    | 'by_question'
    | 'by_section';
}

export async function updateBagelDocument(
  id: string,
  params: UpdateBagelDocumentParams,
): Promise<BagelDocument> {
  const res = await makePatchRequest<{
    message: string;
    payload: BagelDocument;
  }>(`/bagel/documents/${id}`, params);
  return res.data.payload;
}

export async function reprocessBagelDocumentsYear(
  year: string,
): Promise<{ year: string; started: number }> {
  const res = await makePostRequest<{
    message: string;
    payload: { year: string; started: number };
  }>(`/bagel/documents/reprocess/year/${encodeURIComponent(year)}`, {});
  return res.data.payload;
}

export async function uploadBagelDocument(
  params: UploadBagelDocumentParams,
): Promise<BagelDocument> {
  const token = await refreshTokenId();
  const baseUrl = url.API_URL?.replace(/\/$/, '') || '';

  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('name', params.name);
  formData.append('category', params.category);
  if (params.description)
    formData.append('description', params.description);

  const response = await fetch(`${baseUrl}/bagel/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const err = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(err.message || `Upload failed (${response.status})`);
  }

  const data = await response.json();
  return data.payload as BagelDocument;
}
